/**
 * High-level hook that encapsulates all SmartTimeInput logic
 * This hook consolidates state management, parsing, suggestions, validation,
 * keyboard navigation, and accessibility concerns into a single interface.
 */

import type * as React from 'react'
import { useRef, useCallback, useMemo, useEffect, useId } from 'react'
import type { Temporal } from '@js-temporal/polyfill'

import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { useIsMobileDevice } from '../../../hooks/useIsMobileDevice'
import { useTimeParser } from './useTimeParser'
import { useTimeSuggestions } from './useTimeSuggestions'
import { useSmartTimeInputState } from './useSmartTimeInputState'
import { useKeyboardNavigation } from './useKeyboardNavigation'
import { useValueCommit } from './useValueCommit'
import { useComboboxAccessibility } from './useComboboxAccessibility'
import { useValidation } from './useValidation'
import { useChronoLoader } from './useChronoLoader'
import {
  extractPlainTime,
  createPlainTime,
  isValidTimezone,
} from '../utils/temporalHelpers'
import { resolveTimeFormat, getEffectiveStep } from '../utils/formatters'
import type {
  SmartTimeInputProps,
  ValidationError,
  TimeSuggestion,
} from '../types'

export interface UseSmartTimeComboboxReturn {
  // Refs
  inputRef: React.RefObject<HTMLInputElement>
  wrapperRef: React.RefObject<HTMLDivElement>
  lastOpenTimeRef: React.RefObject<number>

  // State values
  inputValue: string
  isOpen: boolean
  activeIndex: number
  isFocused: boolean
  validationError: ValidationError | null
  userNavigated: boolean
  detectedMobile: boolean
  validatedTimezone: string | undefined
  shouldScrollToActive: boolean

  // Computed values
  effectiveValue: Temporal.PlainTime | null
  resolvedFormat: '12' | '24'
  effectiveStep: number
  suggestions: ReturnType<typeof useTimeSuggestions>['suggestions']
  matchIndex: number
  parseResult: ReturnType<ReturnType<typeof useTimeParser>['parseTime']> | null
  isValid: boolean
  placeholderHint: string
  hasError: boolean

  // Handlers
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleNativeTimeChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleSuggestionSelect: (suggestion: TimeSuggestion) => void
  handleFocus: () => void
  handleDropdownOpenChange: (nextOpen: boolean) => void
  handleBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void

  // Accessibility
  listboxId: string
  errorId: string
  liveRegionMessage: string
  inputAriaProps: ReturnType<typeof useComboboxAccessibility>['inputAriaProps']

  // Actions
  actions: ReturnType<typeof useSmartTimeInputState>['actions']

  // Value commit utilities
  formatForDisplay: (time: Temporal.PlainTime) => string
}

export function useSmartTimeCombobox(
  props: SmartTimeInputProps,
): UseSmartTimeComboboxReturn {
  const {
    // Core Props
    value,
    defaultValue,
    onChange,
    onBlur,

    // Display Props
    placeholder = 'Enter time...',
    hourCycle = 12,
    timezone,

    // Constraints
    min,
    max,
    step = 15,
    granularity = 'minute',
    timeOverrides,

    // Behavior Props
    mode = 'smart',
    required = false,

    // Validation Props
    onValidationError,

    // Accessibility Props
    id,
    name,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
  } = props

  // Internal refs
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const lastOpenTimeRef = useRef<number>(0)
  const detectedMobile = useIsMobileDevice()

  // Validate timezone once using isValidTimezone as the single source of truth
  const validatedTimezone = useMemo(
    () => (timezone && isValidTimezone(timezone) ? timezone : undefined),
    [timezone],
  )

  // State management
  const { state, actions } = useSmartTimeInputState(defaultValue)

  const {
    inputValue,
    isOpen,
    activeIndex,
    isFocused,
    internalValue,
    userNavigated,
    shouldScrollToActive,
  } = state

  const effectiveValue = useMemo(() => {
    if (value !== undefined) {
      return value ? extractPlainTime(value) : null
    }
    return internalValue || null
  }, [value, internalValue])

  // Resolve 12/24 format from hourCycle
  const resolvedFormat = useMemo(
    () => resolveTimeFormat(hourCycle),
    [hourCycle],
  )

  // Adjust step based on granularity if user hasn't overridden differently
  const effectiveStep = useMemo(
    () => getEffectiveStep(granularity, step),
    [granularity, step],
  )

  // Chrono loader hook - manages async loading of chrono-node module
  const { loadingState, loadModules } = useChronoLoader()

  // Parser hook - receives pre-validated timezone and chrono module when ready
  const { parseTime, isValidInput } = useTimeParser({
    mode,
    min,
    max,
    timezone: validatedTimezone,
    chronoModule:
      loadingState.status === 'ready' ? loadingState.chrono : undefined,
  })

  // Validation hook - manages validation state
  const {
    validationError,
    validateAndCommitOnBlur,
    setValidationError: setValidationErrorFromHook,
  } = useValidation({
    parseTime,
    isValidInput,
    required,
    onValidationError,
  })

  // Value commit hook
  const { commitValue, formatForDisplay, createRangeError } = useValueCommit({
    value,
    granularity,
    min,
    max,
    resolvedFormat,
    inputValue,
    onChange,
    onValidationError,
    onSetInternalValue: actions.setInternalValue,
    onSetInput: actions.setInput,
    onCloseMenu: actions.closeMenu,
    onSetValidationError: setValidationErrorFromHook,
  })

  // Initialize / sync input value from effective value
  useEffect(() => {
    if (isFocused) {
      return
    }
    const nextValue = effectiveValue ? formatForDisplay(effectiveValue) : ''
    if (nextValue !== inputValue) {
      actions.setInput(nextValue)
    }
  }, [effectiveValue, isFocused, formatForDisplay, inputValue, actions])

  const { suggestions, matchIndex } = useTimeSuggestions({
    input: inputValue,
    hourCycle: resolvedFormat === '12' ? 12 : 24,
    step: effectiveStep,
    min,
    max,
    timeOverrides,
    currentValue: effectiveValue || undefined,
    timezone: validatedTimezone,
  })

  // Debounced parsing
  const debouncedInput = useDebouncedValue(inputValue, 150)

  // Parse debounced input
  const parseResult = useMemo(() => {
    if (!debouncedInput.trim()) {
      return null
    }
    return parseTime(debouncedInput)
  }, [debouncedInput, parseTime])

  // Validation
  const isValid = useMemo(() => {
    if (!inputValue.trim()) {
      return !required
    }
    return isValidInput(inputValue)
  }, [inputValue, required, isValidInput])

  // Handle input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      actions.setInput(newValue, true)
      actions.resetActiveIndex()
      setValidationErrorFromHook(null)
      actions.openMenu()
    },
    [actions, setValidationErrorFromHook],
  )

  const handleNativeTimeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value: nextValue } = event.target
      if (!nextValue) {
        actions.setInput('', true)
        commitValue(null)
        return
      }

      actions.setInput(nextValue, true)

      const [hourPart, minutePart] = nextValue.split(':')
      if (hourPart === undefined || minutePart === undefined) {
        commitValue(null)
        return
      }

      const parsedHour = Number(hourPart)
      const parsedMinute = Number(minutePart)

      if (Number.isNaN(parsedHour) || Number.isNaN(parsedMinute)) {
        commitValue(null)
        return
      }

      const parsedTime = createPlainTime(parsedHour, parsedMinute)
      commitValue(parsedTime)
    },
    [commitValue, actions],
  )

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback(
    (suggestion: TimeSuggestion) => {
      commitValue(suggestion.time)
      inputRef.current?.focus()
    },
    [commitValue],
  )

  // Handle input focus
  const handleFocus = useCallback(() => {
    actions.setFocus(true)
    // Open menu on focus to show suggestions
    // Even if no suggestions yet, they'll appear as user types
    actions.openMenu()

    // Preload chrono-node module on first user interaction (smart mode only)
    // Allow retry if previous load failed
    if (
      mode === 'smart' &&
      (loadingState.status === 'idle' || loadingState.status === 'error')
    ) {
      void loadModules()
    }
  }, [actions, mode, loadingState.status, loadModules])

  const handleDropdownOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        lastOpenTimeRef.current = performance.now()
        actions.openMenu()
        return
      }

      // Ignore an immediate close request right after opening (Radix refocus side-effect)
      const since = performance.now() - lastOpenTimeRef.current
      if (since < 40) {
        return
      }

      // Don't allow Popover to auto-close when input is focused and has suggestions
      // Only allow explicit close actions (blur, Escape key, etc.)
      if (isFocused && suggestions.length > 0) {
        return
      }

      actions.closeMenu()
    },
    [actions, isFocused, suggestions.length],
  )

  // Handle input blur
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const nextFocusTarget = event.relatedTarget
      if (
        nextFocusTarget instanceof HTMLElement &&
        wrapperRef.current?.contains(nextFocusTarget)
      ) {
        return
      }

      actions.setFocus(false)
      actions.closeMenu()

      const isUnchangedFromCommitted =
        effectiveValue !== null &&
        inputValue === formatForDisplay(effectiveValue)
      if (!isUnchangedFromCommitted) {
        validateAndCommitOnBlur(inputValue, commitValue, createRangeError)
      }

      onBlur?.(event)
    },
    [
      inputValue,
      effectiveValue,
      formatForDisplay,
      validateAndCommitOnBlur,
      commitValue,
      createRangeError,
      onBlur,
      actions,
    ],
  )

  // Keyboard navigation hook
  const { handleKeyDown } = useKeyboardNavigation({
    isOpen,
    suggestions,
    activeIndex,
    matchIndex,
    inputValue,
    parseResult,
    effectiveStep,
    min,
    max,
    inputRef,
    isValidInput,
    onOpenMenu: actions.openMenu,
    onCloseMenu: actions.closeMenu,
    onNavigate: actions.navigate,
    onSetActiveIndex: actions.setActiveIndex,
    onSuggestionSelect: handleSuggestionSelect,
    onCommitValue: commitValue,
  })

  // Keep focus on input when menu opens
  useEffect(() => {
    if (isOpen) {
      // Use rAF to run after layout & potential Radix focus shifts
      const frame = requestAnimationFrame(() => {
        inputRef.current?.focus({ preventScroll: true })
      })
      return () => cancelAnimationFrame(frame)
    }
  }, [isOpen])

  // Generate placeholder hint
  const placeholderHint = useMemo(() => {
    if (parseResult?.success && parseResult.time && inputValue.trim()) {
      let preview = parseResult.time
      if (granularity === 'hour') {
        preview = createPlainTime(preview.hour, 0)
      } else if (granularity === 'minute') {
        preview = createPlainTime(preview.hour, preview.minute)
      }
      const formatted = formatForDisplay(preview)
      if (formatted !== inputValue.trim()) {
        return formatted
      }
    }
    // Support placeholder as PlainTime (compat)
    if (placeholder && typeof placeholder !== 'string') {
      return formatForDisplay(placeholder)
    }
    return typeof placeholder === 'string' ? placeholder : 'Enter time...'
  }, [parseResult, inputValue, granularity, formatForDisplay, placeholder])

  // Determine if we should show error state
  const hasError =
    props.error ||
    validationError !== null ||
    (!isValid && inputValue.trim() !== '' && !isFocused)

  // Generate errorId for aria-errormessage wiring
  const errorId = useId()

  // Accessibility hook
  const { listboxId, liveRegionMessage, inputAriaProps } =
    useComboboxAccessibility({
      isOpen,
      activeIndex,
      suggestions,
      suggestionCount: suggestions.length,
      hasError,
      errorId: hasError ? errorId : undefined,
      id,
      name,
      ariaLabel,
      ariaDescribedBy,
    })

  return {
    // Refs
    inputRef,
    wrapperRef,
    lastOpenTimeRef,

    // State values
    inputValue,
    isOpen,
    activeIndex,
    isFocused,
    validationError,
    userNavigated,
    detectedMobile,
    validatedTimezone,
    shouldScrollToActive,

    // Computed values
    effectiveValue,
    resolvedFormat,
    effectiveStep,
    suggestions,
    matchIndex,
    parseResult,
    isValid,
    placeholderHint,
    hasError,

    // Handlers
    handleInputChange,
    handleNativeTimeChange,
    handleSuggestionSelect,
    handleFocus,
    handleDropdownOpenChange,
    handleBlur,
    handleKeyDown,

    // Accessibility
    listboxId,
    errorId,
    liveRegionMessage,
    inputAriaProps,

    // Actions
    actions,

    // Value commit utilities
    formatForDisplay,
  }
}
